import { useEffect, useState, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { AppErrorBoundary } from "@/components/ErrorBoundary";
import { ErrorBoundary } from "react-error-boundary";
import CarRemovalNotification from "@/components/CarRemovalNotification";
import CarChangeNotification from "@/components/CarChangeNotification";

import { useFontLoading } from "@/hooks/useFontLoading";
import { testSupabaseConnection } from "@/lib/supabaseTest";
import { waitForSupabaseSession, checkStorageConsistency, clearStorage } from "@/lib/storage";
import { setupErrorTracking } from "@/lib/error-tracking";

import { useAuth } from "@/contexts/AuthProvider";
import { AuthProvider } from "@/contexts/AuthProvider";
import { CarProvider, useCar } from '@/contexts/CarContext';
import { SettingsProvider } from '@/contexts/SettingsContext';
import { MechanicShareProvider } from '@/contexts/MechanicShareContext';
import { CarSelectionProvider } from '@/contexts/CarSelectionContext';
import { FluidProvider } from '@/contexts/FluidContext';
import { ExpenseCategoriesProvider } from '@/contexts/ExpenseCategoriesContext';
import { RequireAuth } from '@/components/routing/RequireAuth';
import { LanguageProvider } from '@/i18n/LanguageContext';

import { supabase } from "@/lib/supabase";
import { toast } from "@/components/ui/use-toast";

// Pages
import HomePage from "@/pages/HomePage";
import CarsPage from "@/pages/CarsPage";
import AddCarPage from "@/pages/AddCarPage";
import CarDetailPage from "@/pages/CarDetailPage";
import LogSymptomPage from "@/pages/LogSymptomPage";
import ReportsPage from "@/pages/ReportsPage";
import HealthReportPage from "@/pages/HealthReportPage";
import GenerateServiceRequestPage from "@/pages/GenerateServiceRequestPage";
import ServiceRequestViewPage from "@/pages/ServiceRequestViewPage";
import SharedHealthReportPage from "@/pages/SharedHealthReportPage";
import SettingsPage from "@/pages/SettingsPage";
import ProfilePage from "@/pages/ProfilePage";
import SharedAccessPage from "@/pages/SharedAccessPage";
import SymptomSummaryPage from "@/pages/SymptomSummaryPage";
import AddOdometerPage from "@/pages/AddOdometerPage";
import OdometerListPage from "@/pages/OdometerListPage";
import AddCostPage from "@/pages/AddCostPage";
import ExpensesListPage from "@/pages/ExpensesListPage";
import ExpenseViewPage from "@/pages/ExpenseViewPage";
import AddServiceEntryPage from "@/pages/AddServiceEntryPage";
import ServiceEntryViewPage from "@/pages/ServiceEntryViewPage";
import RecordsPage from "@/pages/RecordsPage";
import AddTaskPage from "@/pages/AddTaskPage";
import ActivityPage from "@/pages/ActivityPage";
import CarViewPage from "@/pages/CarViewPage";
import ShareCarPage from "@/pages/ShareCarPage";
import GeolocationTest from "@/pages/GeolocationTest";
import NotFound from "@/pages/NotFound";
import EditCarPage from "@/pages/EditCarPage";
import LogRepairPage from "@/pages/LogRepairPage";
import AddFluidPage from "@/pages/AddFluidPage";
import I18nTestPage from "@/pages/I18nTestPage";
import MechanicEntry from "@/components/mechanic-share/MechanicEntry";
import AIAssistantPage from "@/pages/AIAssistantPage";
import AuthCallback from "@/pages/auth/callback";
import LoginPage from "@/pages/auth/LoginPage";
import AuthErrorPage from "@/pages/auth/AuthErrorPage";
import CreditsPage from "@/pages/CreditsPage";
import {
  WelcomePage,
  SignUpPage,
  AddCarPage as OnboardingAddCarPage,
  TutorialPage,
  CheckEmail,
} from "@/pages/onboarding";
import { ComponentPlayground } from "@/pages/ComponentPlayground";
import NavBar from "@/components/NavBar";

const queryClient = new QueryClient();

const PageErrorBoundary = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary
    FallbackComponent={({ error, resetErrorBoundary }) => (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="max-w-md w-full space-y-4 text-center">
          <h2 className="text-xl font-semibold text-gray-900">This page encountered an error</h2>
          <pre className="text-sm text-red-600 bg-red-50 p-4 rounded-md overflow-auto">
            {error.message}
          </pre>
          <button
            onClick={resetErrorBoundary}
            className="w-full py-2 px-4 bg-lime text-black rounded-md hover:bg-lime/90 transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    )}
    onReset={() => window.location.reload()}
  >
    {children}
  </ErrorBoundary>
);

const ProtectedRoutesWrapper = () => {
  return (
    <div className="pb-16">
      <Outlet />
    </div>
  );
};

const AppRoutes = () => {
  const { user } = useAuth();
  
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Navigate to={user ? "/home" : "/login"} replace />} />
    <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/error" element={<AuthErrorPage />} />
      <Route 
        path="/share/car/:token" 
        element={
          <CarProvider>
            <PageErrorBoundary>
              <ShareCarPage />
            </PageErrorBoundary>
          </CarProvider>
        } 
      />
      <Route 
        path="/share/health-report/:token" 
        element={
          <PageErrorBoundary>
            <SharedHealthReportPage />
          </PageErrorBoundary>
        } 
      />
    {/* Onboarding */}
    <Route path="/onboarding/welcome" element={<WelcomePage />} />
    <Route path="/onboarding/signup" element={<PageErrorBoundary><SignUpPage /></PageErrorBoundary>} />
    <Route path="/onboarding/check-email" element={<CheckEmail />} />
    <Route path="/onboarding/add-car" element={<PageErrorBoundary><OnboardingAddCarPage /></PageErrorBoundary>} />
    <Route path="/onboarding/tutorial" element={<PageErrorBoundary><TutorialPage /></PageErrorBoundary>} />
    {/* Protected */}
    <Route
      element={
        <RequireAuth>
          <CarProvider>
            <CarSelectionProvider>
              <FluidProvider>
                <SettingsProvider>
                  <MechanicShareProvider>
                    <ExpenseCategoriesProvider>
                      <ProtectedRoutesWrapper />
                    </ExpenseCategoriesProvider>
                  </MechanicShareProvider>
                </SettingsProvider>
              </FluidProvider>
            </CarSelectionProvider>
          </CarProvider>
        </RequireAuth>
      }
    >
      <Route path="/home" element={<HomePage />} />
      <Route path="/cars" element={<CarsPage />} />
      <Route path="/cars/new" element={<CarDetailPage />} />
      <Route path="/cars/:carId" element={<CarDetailPage />} />
      <Route path="/cars/add" element={<AddCarPage />} />
      <Route path="/cars/edit/:carId" element={<EditCarPage />} />
      <Route path="/cars/:carId/fluids/add" element={<AddFluidPage />} />
      <Route path="/car-view/:carId" element={<CarViewPage />} />
      <Route path="/log-symptom" element={<LogSymptomPage />} />
      <Route path="/log-repair/:carId" element={<LogRepairPage />} />
      <Route path="/log-repair/:carId/:repairId" element={<LogRepairPage />} />
      <Route path="/symptom-summary/:carId/:issueId" element={<SymptomSummaryPage />} />
      <Route path="/records/:carId?" element={<RecordsPage />} />
      <Route path="/odometer/add" element={<AddOdometerPage />} />
      <Route path="/odometer/list/:carId" element={<OdometerListPage />} />
      <Route path="/expenses/add" element={<AddCostPage />} />
      <Route path="/expenses/edit/:expenseId" element={<AddCostPage />} />
      <Route path="/expenses/view/:expenseId" element={<ExpenseViewPage />} />
      <Route path="/expenses/list/:carId" element={<ExpensesListPage />} />
      <Route path="/services/add" element={<AddServiceEntryPage />} />
      <Route path="/services/view/:entryId" element={<ServiceEntryViewPage />} />
      <Route path="/tasks/add" element={<AddTaskPage />} />
      <Route path="/activity/:carId" element={<ActivityPage />} />
      <Route path="/reports" element={<ReportsPage />} />
      <Route path="/reports/health/:carId?" element={<HealthReportPage />} />
      <Route path="/service-request/generate/:carId?" element={<GenerateServiceRequestPage />} />
      <Route path="/service-request/view/:requestId" element={<ServiceRequestViewPage />} />
      <Route path="/settings" element={<SettingsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/shared-access" element={<SharedAccessPage />} />
                {/* Temporary i18n test route - remove before production */}
                <Route path="/i18n-test" element={<I18nTestPage />} />
      <Route path="/geolocation-test" element={<GeolocationTest />} />
      <Route path="/mechanic-share" element={<MechanicEntry />} />
      <Route path="/assistant/:carId?" element={<AIAssistantPage />} />
      <Route path="/credits" element={<CreditsPage />} />
      <Route path="*" element={<NotFound />} />
    </Route>
  </Routes>
  );
};

const AppInner = () => {
  const { loading: authLoading } = useAuth();
  const fontsLoaded = useFontLoading();
  const [isChecking, setIsChecking] = useState(true);
  const [stylesLoaded, setStylesLoaded] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log(`🧠 Auth event after reload: ${event}`, session ? 'session exists' : 'no session');
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        setupErrorTracking();

        // Support preview/marketing entrypoints that need to deep-link into the SPA.
        // Example: /?returnTo=/share/car/<token>
        try {
          const params = new URLSearchParams(window.location.search);
          const returnTo = params.get('returnTo');
          if (returnTo && returnTo.startsWith('/')) {
            localStorage.setItem('auth-return-to', returnTo);
            // Use a full navigation so BrowserRouter mounts on the desired path.
            window.location.replace(returnTo);
            return;
          }
        } catch {}

        if (window.location.pathname !== "/auth/callback") {
          if (import.meta.env.DEV) {
            console.log("🧭 Current path:", window.location.pathname);
            console.log("🔍 Checking localStorage key:", "app-supabase-auth");
            console.log("📦 localStorage[app-supabase-auth]:", localStorage.getItem("app-supabase-auth"));
          }

          const session = await waitForSupabaseSession();
          console.log(session ? "✅ Supabase session restored" : "❌ Supabase session not restored");

          const isConsistent = await checkStorageConsistency(session);
          if (!isConsistent && import.meta.env.MODE === 'production') {
            await clearStorage();
          }

          await testSupabaseConnection(session);
        }
      } catch (error) {
        console.error("Initialization error:", error);
        toast({
          title: "Error",
          description: "Failed to initialize the application. Please refresh the page.",
          variant: "destructive",
        });
      } finally {
        setIsChecking(false);
      }
    };

    init();
  }, []);

  useEffect(() => {
    const checkStyles = () => {
      const styleSheets = document.styleSheets;
      for (let i = 0; i < styleSheets.length; i++) {
        try {
          const rules = styleSheets[i].cssRules;
          if (rules && rules.length > 0) {
            setStylesLoaded(true);
            return;
          }
        } catch {}
      }
      requestAnimationFrame(checkStyles);
    };
    checkStyles();
  }, []);

  if (!fontsLoaded || !stylesLoaded || isChecking || authLoading) return <LoadingScreen />;

  const isDev = window.location.pathname.startsWith("/dev/components") || window.location.pathname.startsWith("/components");

  console.log("🧭 Current path:", window.location.pathname);
  console.log(" isDev:", isDev);

  return (
    <AppErrorBoundary>
      {isDev ? (
        <TooltipProvider>
          <ComponentPlayground />
        </TooltipProvider>
      ) : (
        <Suspense fallback={<LoadingScreen />}>
          <AppRoutes />
        </Suspense>
      )}
    </AppErrorBoundary>
  );
};

const App = () => {
  const fontsLoaded = useFontLoading();
  const [stylesLoaded, setStylesLoaded] = useState(false);

  useEffect(() => {
    setupErrorTracking();
  }, []);

  useEffect(() => {
    const checkStyles = () => {
      const styleSheets = document.styleSheets;
      for (let i = 0; i < styleSheets.length; i++) {
        try {
          const rules = styleSheets[i].cssRules;
          if (rules && rules.length > 0) {
            setStylesLoaded(true);
            return;
          }
        } catch {}
      }
      requestAnimationFrame(checkStyles);
    };
    checkStyles();
  }, []);

  if (!fontsLoaded || !stylesLoaded) return <LoadingScreen />;

  return (
    <QueryClientProvider client={queryClient}>
      <AppErrorBoundary>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AuthProvider>
            <LanguageProvider>
              <AppInner />
              <Toaster />
              <Sonner />
              <CarRemovalNotification />
              <CarChangeNotification />
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </AppErrorBoundary>
    </QueryClientProvider>
  );
};

export default App;
